/**
 * Client-side FlightOption model with custom methods and computed properties
 */

import type { AirlineInfo } from '@elara/domain/models/schemas/flight-schemas';

import type { FirestoreContainer } from '#containers/FirestoreContainer.js';
import type { ClientInstanceOf } from '#firebase/types.js';
import type { MapPoint } from '@elara/client-core/ui-contracts/map-point';
import type { FlightSegmentModel } from './FlightSegmentModel.js';

import { FlightOptionModelSpec } from '@elara/domain/models/FlightOptionModelSpec';

import { createClientModel } from '#firebase/createClientModel.js';
import { getOriginalCost, getPriceDelta, isPriceStale, isTravelDatePassed } from '#lib/price-utils.js';
import { formatPrice } from '#lib/utils.js';

import { FlightSegmentModelFactory } from './FlightSegmentModel.js';

export type FlightDirection = 'outbound' | 'return' | 'all';

/**
 * FlightOption model for client-side usage.
 * Use .get({ tripId, id }) to load a stored flight option.
 */
export function createClientFlightOptionModel(firestore: FirestoreContainer) {
  const model = createClientModel(
    firestore,
    FlightOptionModelSpec,
    () => ({
      get outboundFlights(): FlightSegmentModel[] {
        return FlightSegmentModelFactory.fromMany(this.segments ?? this.outboundSegments ?? []);
      },

      get returnFlights(): FlightSegmentModel[] {
        return FlightSegmentModelFactory.fromMany(this.returnSegments ?? []);
      },

      get allFlights(): FlightSegmentModel[] {
        return [...this.outboundFlights, ...this.returnFlights];
      },

      flightsForDirection(direction: FlightDirection): FlightSegmentModel[] {
        if (direction === 'all') return this.allFlights;
        return direction === 'outbound' ? this.outboundFlights : this.returnFlights;
      },

      firstFlight(direction: FlightDirection): FlightSegmentModel | undefined {
        return this.flightsForDirection(direction)[0];
      },

      lastFlight(direction: FlightDirection): FlightSegmentModel | undefined {
        return this.flightsForDirection(direction).at(-1);
      },

      totalTravelTime(direction: FlightDirection): number {
        return (
          this.totalLayoverTime(direction) +
          this.flightsForDirection(direction).reduce((acc, f) => acc + (f.duration ?? 0), 0)
        );
      },

      totalLayoverTime(direction: FlightDirection): number {
        const flights = this.flightsForDirection(direction);
        if (flights.length <= 1) return 0;
        let total = 0;
        for (let i = 0; i < flights.length - 1; i++) {
          const cur = flights[i];
          const next = flights[i + 1];
          if (cur && next) total += cur.getLayoverTime(next);
        }
        return total;
      },

      hasConnections(direction: FlightDirection): boolean {
        return this.flightsForDirection(direction).length > 1;
      },

      stopCount(direction: FlightDirection): number {
        return Math.max(0, this.flightsForDirection(direction).length - 1);
      },

      isDirect(direction: FlightDirection): boolean {
        return this.stopCount(direction) === 0;
      },

      primaryAirline(direction: FlightDirection): AirlineInfo | undefined {
        return this.flightsForDirection(direction)[0]?.airline;
      },

      uniqueAirlineNames(direction: FlightDirection): string[] {
        const flights = this.flightsForDirection(direction);
        return [...new Set(flights.map((f) => f.airline.name).filter(Boolean))];
      },

      formattedAirlineNames(direction: FlightDirection): string {
        const airline = this.primaryAirline(direction);
        if (!airline?.name) return '';
        const names = this.uniqueAirlineNames(direction);
        if (names.length > 1) return `${airline.name} +${names.length - 1}`;
        return airline.name;
      },

      get totalPassengers(): number {
        const p = this.passengers ?? { adults: 1, children: 0, infants: 0 };
        return p.adults + p.children + p.infants;
      },

      get totalPaidPassengers(): number {
        const p = this.passengers ?? { adults: 1, children: 0, infants: 0 };
        return p.adults + p.children;
      },

      get formattedTotalPrice(): string {
        const cost = this.cost;
        if (!cost) return '';
        return formatPrice(cost.amount, cost.currency);
      },

      get formattedPricePerPerson(): string {
        const cost = this.cost;
        if (!cost) return '';
        return formatPrice(cost.amount / this.totalPaidPassengers, cost.currency);
      },

      get formattedSearchType(): string {
        return this.roundTrip ? 'Round trip' : 'One-way';
      },

      get mapPoints(): MapPoint[] {
        // A connecting airport appears twice (as one leg's destination and the next leg's origin) —
        // dedupe by `id`, not by reference: each call rebuilds fresh `MapPoint` objects, so wrapping
        // them in a `Set` (as before) never actually collapsed the duplicate, leaving two React
        // elements with the same key wherever this list gets rendered (e.g. the route map's markers).
        const byId = new Map<string, MapPoint>();
        for (const point of this.allFlights.flatMap((f) => f.mapPoints)) {
          if (!byId.has(point.id)) byId.set(point.id, point);
        }
        return [...byId.values()];
      },

      formattedTravelClass(direction: FlightDirection): string | undefined {
        const classes = [...new Set(this.flightsForDirection(direction).map((f) => f.travelClass))];
        if (classes.length > 1) return `${classes[0]} +${classes.length - 1}`;
        return classes[0];
      },

      get isAvailable(): boolean {
        return this.available !== false;
      },

      // Compound, read-time staleness: a departed trip never needs a price recheck, so
      // short-circuit before the time-threshold check. Never denormalized (goes stale on read).
      get isStale(): boolean {
        if (this.isTravelDatePassed) return false;
        return isPriceStale(this.fetchedAt);
      },

      /** True if the departure date is today or in the past. */
      get isTravelDatePassed(): boolean {
        const firstSegment = this.outboundSegments?.[0] ?? this.segments?.[0];
        return isTravelDatePassed(firstSegment?.departure);
      },

      get originalCost(): ReturnType<typeof getOriginalCost> {
        return getOriginalCost(this.priceHistory);
      },

      get priceDelta(): ReturnType<typeof getPriceDelta> {
        return getPriceDelta(this.cost ?? undefined, this.originalCost);
      },

      get hasActions(): boolean {
        return this.roundTrip === true ? this.returnFlights.length > 0 : this.outboundFlights.length > 0;
      },

      get costDescription(): string {
        return this.totalPaidPassengers > 1
          ? `${this.formattedTotalPrice} for ${this.totalPassengers} passengers`
          : 'Per passenger';
      },

      displayTitle(direction: FlightDirection): string {
        const flights = this.flightsForDirection(direction);
        if (this.roundTrip) return `Return flight from ${this.firstFlight('outbound')?.originCode} to ${this.lastFlight('outbound')?.destinationCode}`;
        if (flights.length === 0) return 'Flight';
        const airline = this.primaryAirline(direction)?.name;
        const dep = this.firstFlight(direction)?.originCode;
        const arr = this.lastFlight(direction)?.destinationCode;
        if (!dep || !arr) return `${airline} flight`;
        return `${airline} flight from ${dep} to ${arr}`;
      },

      getLayoverTimeBetweenFlights(direction: FlightDirection, flightIndex: number): number {
        const flights = this.flightsForDirection(direction);
        if (flightIndex < 0 || flightIndex >= flights.length - 1) return 0;
        const cur = flights[flightIndex];
        const next = flights[flightIndex + 1];
        return cur && next ? cur.getLayoverTime(next) : 0;
      },
    }),
    {
      // No flows attached - flight options are created by server tools
    },
  );
  return model;
}

export type ClientFlightOptionModel = ReturnType<typeof createClientFlightOptionModel>;
export type FlightOptionModel = ClientInstanceOf<ClientFlightOptionModel>;
